import './styles.scss'
import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { getLocalUserDetails, getLocalTheme } from 'Utils/LocalStorage_Handler'
import { getAllShortNewsForSubForCountry, getAllFreeTrendingNewsForGuest } from 'Services/apiAdapter_CoffeeWeb'
import { provisionUserNewsReminders, deliverPendingGlobalResponses, getUserNewsReminders, getGuestUserNewsReminders, getNewsReminderHistory, updateNewsReminder, deleteNewsReminder, markNewsReminderLastRead } from 'Services/Supabase'
import { supabaseWeather } from 'Services/supabaseClient'
import CreateAccountInfoModal from 'Components/UIComponent/CreateAccountInfoModal'
import { ROUTE_STRINGS } from 'Utils/Constants'
import { YourNewsHeader, YourNewsSidebar, YourNewsContent, EditReminderModal } from './Components'
import aiResponse from './Utils/aiResponse.json'

const YourNews = () => {
  const userDetails = getLocalUserDetails()
  const userId = userDetails?.id != null ? String(userDetails.id) : null
  const subscriptionType = userDetails?.sub?.subType ?? 1
  const { t } = useTranslation()
  const selectedReminderIdRef = useRef(null)
  const initialAutoReadDoneRef = useRef(false)

  const [reminders, setReminders] = useState([])
  const [selectedReminderId, setSelectedReminderId] = useState(null)
  const [selectedHistoryEntries, setSelectedHistoryEntries] = useState([])
  const [selectedReminderTitle, setSelectedReminderTitle] = useState('')
  const [shortNews, setShortNews] = useState([])
  const [loading, setLoading] = useState(true)
  const [hasInitialHistoryLoaded, setHasInitialHistoryLoaded] = useState(false)
  const [showGuidelines, setShowGuidelines] = useState(false)
  const [theme, setTheme] = useState(() => getLocalTheme() || 'light')
  const [editReminder, setEditReminder] = useState(null)
  // Commented as per requirement - Guidelines modal functionality
  // const [guidelinesVisible, setGuidelinesVisible] = useState(false)

  selectedReminderIdRef.current = selectedReminderId

  const getButtonAndInfo = () => ({
    buttonText: t('LOGIN_AND_CREATE_ACCOUNT'),
    infoMessage: t('TO_ACCESS_MORE_FEATURE_CREATE_ACCOUNT'),
    className: 'login-button',
    navigationPath: ROUTE_STRINGS.login,
    state: {}
  })

  const { buttonText, infoMessage, className, navigationPath } = getButtonAndInfo()

  const loadReminders = useCallback(
    async (opts = {}) => {
      const silent = opts.silent === true

      if (!silent) setLoading(true)
      try {
        const { success, reminders: list } = userDetails?.id ? await getUserNewsReminders(userId) : await getGuestUserNewsReminders()

        setReminders(success && Array.isArray(list) ? list : [])
      } catch (e) {
        console.warn('YourNews: load reminders error', e)
        setReminders([])
      } finally {
        if (!silent) setLoading(false)
      }
    },
    [userId]
  )

  useEffect(() => {
    let cancelled = false
    const init = async () => {
      initialAutoReadDoneRef.current = false
      try {
        if (userId) {
          await provisionUserNewsReminders(userId, subscriptionType)
          if (cancelled) return
          await deliverPendingGlobalResponses(userId)
          if (cancelled) return
          await loadReminders()
        } else {
          await loadReminders()
        }
      } catch (e) {
        if (!cancelled) setLoading(false)
      }
    }

    init()

    return () => {
      cancelled = true
    }
  }, [userId, subscriptionType, loadReminders])

  // Realtime Updating of reminders and history when new reminder is added or read state changes
  useEffect(() => {
    if (!userId) return

    const channelName = `your_news_updates:${userId}`
    const channel = supabaseWeather.channel(channelName)

    const onRealtimeUnreadChange = async (payload) => {
      const changedTable = payload?.table

      if (changedTable === 'news_reminder_read_state') {
        await loadReminders({ silent: true })

        return
      }

      try {
        await loadReminders()
        const currentReminderId = selectedReminderIdRef.current

        if (currentReminderId) {
          const { success, history } = await getNewsReminderHistory(currentReminderId, userId, 50, 0)

          setSelectedHistoryEntries((prev) => (success && Array.isArray(history) ? history : prev))
        }
      } catch (e) {
        console.warn('YourNews: realtime refresh error', e)
      }
    }

    channel
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'news_reminder_history',
          filter: `user_id=eq.${userId}`
        },
        onRealtimeUnreadChange
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'news_reminder_read_state',
          filter: `user_id=eq.${userId}`
        },
        onRealtimeUnreadChange
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'news_reminder_read_state',
          filter: `user_id=eq.${userId}`
        },
        onRealtimeUnreadChange
      )
      .subscribe((status, err) => {
        if (err) {
          console.warn('YourNews: realtime subscribe error', err)
        }
      })

    return () => {
      supabaseWeather.removeChannel(channel)
    }
  }, [loadReminders])

  useEffect(() => {
    if (reminders.length > 0 && selectedReminderId == null && !showGuidelines) {
      const firstId = reminders[0].id

      setSelectedReminderId(firstId)
    }
  }, [reminders, selectedReminderId, userId, showGuidelines])

  useEffect(() => {
    fetchShortNews()
  }, [userId])

  useEffect(() => {
    const updateTheme = () => {
      const theme = getLocalTheme()

      setTheme(theme)
    }

    window.addEventListener('themeChanged', updateTheme)

    return () => {
      window.removeEventListener('themeChanged', updateTheme)
    }
  }, [])

  useEffect(() => {
    if (userDetails?.id) {
      if (!selectedReminderId) {
        setSelectedHistoryEntries([])
        setSelectedReminderTitle('')
        setHasInitialHistoryLoaded(false)

        return
      }
    }
    const currentReminder = userDetails?.id ? reminders.find((r) => r.id === selectedReminderId) : reminders[1]
    const reminderTitle = userDetails?.id ? (currentReminder?.title ?? '') : (currentReminder?.default_title ?? '')

    setSelectedReminderTitle(reminderTitle)

    let cancelled = false
    const fetchHistory = async () => {
      try {
        let response

        if (userDetails?.id) {
          response = await getNewsReminderHistory(selectedReminderId, userId, 50, 0)
        } else {
          response = {
            success: true,
            history: aiResponse.aiResponse.ai_response.map((item) => ({
              ...item,
              ai_response: item?.ai_response || ''
            }))
          }
        }

        if (cancelled) return
        const { success, history } = response
        const entries = success && Array.isArray(history) ? history : []

        setSelectedHistoryEntries(entries)
        setHasInitialHistoryLoaded(true)

        if (userDetails?.id && selectedReminderId && !initialAutoReadDoneRef.current) {
          initialAutoReadDoneRef.current = true
          markNewsReminderLastRead(selectedReminderId, userId)
          setReminders((prev) => prev.map((reminder) => (reminder.id === selectedReminderId ? { ...reminder, unread_count: 0 } : reminder)))
        }
      } catch {
        setLoading(false)
      } finally {
        setLoading(false)
      }
    }

    fetchHistory()

    return () => {
      cancelled = true
    }
  }, [selectedReminderId, userId])

  const fetchShortNews = async () => {
    const trendingNewsResponse = userDetails?.id ? await getAllShortNewsForSubForCountry(userId) : await getAllFreeTrendingNewsForGuest()
    const data = trendingNewsResponse?.data?.returnLst

    if (data?.length) {
      setShortNews(data)
    }
  }

  const handleSelectReminder = useCallback(
    ({ id }) => {
      if (selectedReminderId === id && !showGuidelines) return
      setHasInitialHistoryLoaded(false)
      setShowGuidelines(false)
      setSelectedReminderId(id)

      if (userId && id) {
        markNewsReminderLastRead(id, userId)
        setReminders((prev) => prev.map((reminder) => (reminder.id === id ? { ...reminder, unread_count: 0 } : reminder)))
      }
    },
    [selectedReminderId, showGuidelines, setShowGuidelines, userId, setReminders]
  )

  const handleEditReminder = useCallback((reminder) => {
    if (reminder && typeof reminder === 'object') {
      setEditReminder(reminder)
    }
  }, [])

  const handleToggleActive = useCallback(
    async ({ id, isActive }) => {
      if (!userId) return
      const { success } = await updateNewsReminder({
        p_reminder_id: id,
        p_user_id: userId,
        p_title: null,
        p_prompt: null,
        p_reminder_time: null,
        p_repeat_type: null,
        p_custom_days: null,
        p_is_active: isActive
      })

      if (success) {
        await loadReminders()
        if (isActive) {
          await deliverPendingGlobalResponses(userId)
          if (selectedReminderId === id) {
            const { success, history } = await getNewsReminderHistory(id, userId, 50, 0)

            setSelectedHistoryEntries(success && Array.isArray(history) ? history : [])
          }
        }
      }
    },
    [userId, selectedReminderId, loadReminders]
  )

  const handleDeleteReminder = useCallback(
    async ({ id }) => {
      if (!userId) return
      const { success } = await deleteNewsReminder(id, userId)

      if (success) {
        if (selectedReminderId === id) {
          setSelectedReminderId(null)
          setSelectedHistoryEntries([])
          setSelectedReminderTitle('')
        }
        await loadReminders()
      }
    },
    [userId, selectedReminderId, loadReminders]
  )

  const selectedReminder = userDetails?.id ? reminders.find((r) => r.id === selectedReminderId) : { ...reminders[0], next_run_at: null, reminder_time: null, global_reminder_time: null, repeat: null, created_at: null }

  return (
    <div className="your-news-screen" data-testid="your-news-screen">
      <YourNewsHeader shortNewsItems={shortNews} />

      <div className="your-news-screen__body">
        <YourNewsSidebar
          reminders={reminders}
          selectedReminderId={selectedReminderId}
          showGuidelines={showGuidelines}
          subscriptionType={subscriptionType}
          onSelectReminder={handleSelectReminder}
          onEditReminder={handleEditReminder}
          onToggleActive={handleToggleActive}
          onDeleteReminder={handleDeleteReminder}
          onOpenGuidelines={() => {
            setShowGuidelines(true)
            setSelectedReminderId(null)
          }}
          isLoading={loading}
        />
        <main className="your-news-screen__main">
          {userDetails?.id ? (
            <YourNewsContent reminderTitle={selectedReminderTitle} selectedReminder={selectedReminder} historyEntries={selectedHistoryEntries} emptyMessage="Currently no news is available for this tab." showGuidelines={showGuidelines} isLoading={loading || (!showGuidelines && ((reminders.length > 0 && !selectedReminderId) || (!!selectedReminderId && !hasInitialHistoryLoaded)))} />
          ) : (
            <div className="your-news-guest-screen">
              <div className="your-news-guest-screen__page">
                <YourNewsContent reminderTitle={selectedReminderTitle} selectedReminder={selectedReminder} historyEntries={selectedHistoryEntries} />
              </div>
            </div>
          )}
        </main>
      </div>

      <EditReminderModal visible={editReminder != null} reminder={editReminder} userId={userId} onClose={() => setEditReminder(null)} onSuccess={() => loadReminders()} />
      {!userDetails?.id && (
        <div className="your-news-guest-screen__modal">
          <CreateAccountInfoModal buttonText={buttonText} infoMessage={infoMessage} className={className} navigationPath={navigationPath} />
        </div>
      )}
    </div>
  )
}

export default YourNews
